/**
 * How the orchestrator (`parseConnectionString.ts`) chooses among several valid readings of one `key=value` string.
 *
 * Readings for the SAME engine must fill the same target and security settings (review round 5, P0). SqlClient and ODBC
 * both read `Server=sql.example;UID=alice;PWD=…;Encrypt=yes;Encrypt=no` — no `Driver=` tells them apart — but ODBC uses a
 * repeated keyword's first occurrence and SqlClient its last (`repeatedKeywords.ts`): one reads Encrypt on, the other
 * off. Neither the pasting form's engine nor any evidence chooses between two readings of one engine, so when that
 * engine's reading is the one to use, the string is refused, naming the keywords it repeats (never a value). Compared:
 * the server (with its `\instance`), port, database, user, authentication, Encrypt, Trust server certificate (and
 * strict), SSL, an unreadable TLS value and a problem. The secrets are compared across every reading before this.
 *
 * Readings for DIFFERENT engines are not compared: each is that engine's own driver reading the string (Npgsql splits
 * `Host=db:5433` into host and port, MySqlConnector keeps it whole), and the engine is chosen explicitly — the pasting
 * form, the "Reads as" choice — or by engine-only evidence: what the server value names (`serverEvidence`).
 */
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { scanDotNet, scanFailed, type KeyValuePair } from '@/core/utils/connectionStringTokens';
import type { ParserMatch, ReadingFormat } from './types';
import { ENGINE_OF_READING, type Reading } from './connectionStringReadings';
import { keywordOf, MSSQL_KEYWORD_RULES } from './repeatedKeywords';

/** Azure host suffixes that name one engine — engine-only evidence (the ruling after r5). */
const AZURE_HOST_ENGINES: ReadonlyArray<[RegExp, DatabaseEngine]> = [
  [/\.database\.windows\.net$/iu, 'mssql'],
  [/\.postgres\.database\.azure\.com$/iu, 'pgsql'],
  [/\.mysql\.database\.azure\.com$/iu, 'mysql'],
];

/**
 * SqlClient's own server syntax — a protocol prefix (`tcp:`, `np:`, `lpc:`, `admin:`), `host\instance` or a pipe path,
 * `(localdb)`, `host,port` — which neither Npgsql nor MySqlConnector gives a meaning (a comma separates their hosts).
 */
const SQL_SERVER_SYNTAX = /^\s*(?:(?:tcp|np|lpc|admin):|\(localdb\))|\\|,\s*\d+\s*$/iu;

/** The one engine a server value names by itself (an Azure host suffix, SqlClient's server syntax), if any. */
export function serverEvidence(server: unknown): DatabaseEngine | undefined {
  if (typeof server !== 'string') return undefined;
  const host = server.trim().replace(/^(?:tcp|np|lpc|admin):/iu, '').replace(/[:,]\s*\d+$/u, '');
  return AZURE_HOST_ENGINES.find(([suffix]) => suffix.test(host))?.[1] ?? (SQL_SERVER_SYNTAX.test(server) ? 'mssql' : undefined);
}

const COMPARED = ['server', 'port', 'database', 'login', 'authentication', 'encrypt', 'trustServerCertificate', 'ssl'] as const;

/** What a reading settles that two readings of one engine may not disagree about. */
function settled(match: ParserMatch): string {
  return JSON.stringify([...COMPARED.map((field) => match.values[field] ?? null), match.encryptStrict ?? false, match.tlsUnreadable ?? null, match.problem ?? null]);
}

/** Two of `engine`'s valid readings that settle the string differently, or undefined. */
export function disagreementFor(readings: readonly Reading[], engine: DatabaseEngine): [ReadingFormat, ReadingFormat] | undefined {
  const own = readings.filter((reading) => ENGINE_OF_READING[reading.format] === engine);
  const other = own.find((reading) => settled(reading.match) !== settled(own[0].match));
  return other ? [own[0].format, other.format] : undefined;
}

type SqlServerRules = 'sqlclient' | 'odbc';

/** The keywords (folded by each format's synonyms) that feed a compared field. */
const NAMED: Readonly<Record<SqlServerRules, ReadonlySet<string>>> = {
  sqlclient: new Set(['data source', 'initial catalog', 'user id', 'integrated security', 'encrypt', 'trust server certificate']),
  odbc: new Set(['server', 'address', 'database', 'uid', 'trusted_connection', 'encrypt', 'trustservercertificate']),
};

/** One setting: a keyword with its synonyms, and with ODBC an overriding keyword with the one it overrides (Address / Server). */
function settingOf(format: SqlServerRules, key: string): string {
  const keyword = keywordOf(format, key);
  return MSSQL_KEYWORD_RULES[format].overrides?.find(([, overridden]) => overridden === keyword)?.[0] ?? keyword;
}

/**
 * The settings a SQL Server string gives more than once with different values, by either driver's grouping — each as its
 * keys as written (keyword names, case-insensitively once each), synonyms together: `[['Encrypt']]`,
 * `[['Address', 'Server']]`. Only the keywords of compared fields; never a value.
 */
export function repeatedKeywordsIn(text: string): string[][] {
  const groups: string[][] = [];
  const seen = new Set<string>();
  for (const format of ['sqlclient', 'odbc'] as const) {
    const scan = scanDotNet(text, format);
    if (scanFailed(scan)) continue;
    const bySetting = new Map<string, KeyValuePair[]>();
    for (const pair of scan) {
      if (!NAMED[format].has(keywordOf(format, pair.key))) continue;
      const setting = settingOf(format, pair.key);
      bySetting.set(setting, [...(bySetting.get(setting) ?? []), pair]);
    }
    for (const pairs of bySetting.values()) {
      if (new Set(pairs.map((pair) => pair.value.trim().toLowerCase())).size < 2) continue;
      const keys = pairs.map((pair) => pair.key.trim()).filter((key, i, all) => all.findIndex((k) => k.toLowerCase() === key.toLowerCase()) === i);
      const id = keys.map((key) => key.toLowerCase()).sort().join('\n');
      if (!seen.has(id)) groups.push(keys);
      seen.add(id);
    }
  }
  return groups;
}
